import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Form, ProgressBar, Spinner, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import axios from 'axios'
import * as XLSX from 'xlsx'

import { URL as BACKEND_URL, fetchNewToken } from '../../gql/client'
import { STORAGE_NAMES } from '../../helpers/consts'
import { MessageDescriptor, useIntl, FormattedMessage } from 'react-intl'
import {
  CAMPAIGN_MESSAGES as M,
  CSV_PHONE_FIELD,
  DIALER_POLICYDIAL,
  DIALER_VICIDIAL,
  ELLIPSIS,
  EM_DASH,
} from './campaignMessages'

const MAX_ROWS = 50000
const CHUNK_SIZE = 500

type FieldKey =
  | ''
  | 'phone'
  | 'first_name'
  | 'last_name'
  | 'name'
  | 'email'
  | 'state'
  | 'postal_code'
  | 'comments'

const FIELD_OPTIONS: { value: FieldKey; label: MessageDescriptor }[] = [
  { value: '', label: M.fileFieldIgnore },
  { value: 'phone', label: M.fileFieldPhone },
  { value: 'first_name', label: M.firstName },
  { value: 'last_name', label: M.lastName },
  { value: 'name', label: M.fileFieldFullName },
  { value: 'email', label: M.email },
  { value: 'state', label: M.state },
  { value: 'postal_code', label: M.fileFieldPostalCode },
  { value: 'comments', label: M.fileFieldComments },
]

interface ImportResult {
  row_index: number
  phone?: string
  name?: string
  status: 'added' | 'failed' | 'skipped'
  reason?: string
  vicidial_lead_id?: string
}

interface Props {
  campaignId: string
  campaignName: string
  // Required so VICIdial puts leads in the correct hopper instead of falling
  // back to an arbitrary default list. The parent owns the dropdown so all
  // push flows (selected / filtered / from file) share the same selection.
  listId: string
  hasLists: boolean
  // Fired while a push is in flight so the parent can lock tab switching
  // and avoid unmounting the panel mid-upload.
  onPushingChange?: (pushing: boolean) => void
}

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

const authPost = async (url: string, data: any) => {
  try {
    return await axios.post(url, data, { headers: getAuthHeaders() })
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        return await axios.post(url, data, {
          headers: { Authorization: `Bearer ${newToken}` },
        })
      }
    }
    throw err
  }
}

const guessField = (header: string): FieldKey => {
  const h = header.trim().toLowerCase().replace(/[\s_-]+/g, '')
  if (!h) return ''
  if (h.includes('phone') || h.includes('mobile') || h.includes('cell') || h === 'tel' || h === 'telephone') return 'phone'
  if (h === 'firstname' || h === 'fname' || h === 'givenname') return 'first_name'
  if (h === 'lastname' || h === 'lname' || h === 'surname' || h === 'familyname') return 'last_name'
  if (h === 'name' || h === 'fullname') return 'name'
  if (h.includes('email') || h === 'mail') return 'email'
  if (h === 'state' || h === 'st' || h === 'province') return 'state'
  if (h.includes('zip') || h.includes('postal') || h === 'postcode') return 'postal_code'
  if (h.includes('comment') || h.includes('note') || h === 'notas' || h === 'comentarios') return 'comments'
  return ''
}

export const ImportFromFilePanel = ({ campaignId, campaignName, listId, hasLists, onPushingChange }: Props) => {
  const intl = useIntl()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [fileName, setFileName] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<Record<string, any>[]>([])
  const [mapping, setMapping] = useState<Record<string, FieldKey>>({})
  const [pushing, setPushing] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [results, setResults] = useState<ImportResult[] | null>(null)
  const [parseError, setParseError] = useState('')

  // Notify the parent and warn before unload while a push is in flight, so a
  // tab switch / refresh / browser close cannot silently truncate the upload.
  useEffect(() => {
    onPushingChange?.(pushing)
    if (!pushing) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [pushing, onPushingChange])

  const reset = useCallback(() => {
    setFileName('')
    setHeaders([])
    setRows([])
    setMapping({})
    setPushing(false)
    setProgress(null)
    setResults(null)
    setParseError('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const handleFile = (file: File) => {
    setParseError('')
    setResults(null)
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const wb = XLSX.read(data, { type: 'array' })
        const firstSheet = wb.Sheets[wb.SheetNames[0]]
        if (!firstSheet) {
          setParseError('The file has no sheets')
          return
        }
        const parsed: Record<string, any>[] = XLSX.utils.sheet_to_json(firstSheet, {
          defval: '',
          raw: false,
        })
        if (parsed.length === 0) {
          setParseError('The file is empty')
          return
        }
        const cols = Object.keys(parsed[0])
        const auto: Record<string, FieldKey> = {}
        cols.forEach((c) => {
          auto[c] = guessField(c)
        })
        setFileName(file.name)
        setHeaders(cols)
        setRows(parsed)
        setMapping(auto)
      } catch (err: any) {
        setParseError(intl.formatMessage(M.fileParseError, { message: err?.message || String(err) }))
      }
    }
    reader.onerror = () => setParseError(intl.formatMessage(M.fileReadError))
    reader.readAsArrayBuffer(file)
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  const mappedRows = useMemo(() => {
    if (rows.length === 0) return []
    return rows.map((row) => {
      const out: Record<string, any> = {}
      headers.forEach((h) => {
        const target = mapping[h]
        if (target) out[target] = row[h]
      })
      return out
    })
  }, [rows, headers, mapping])

  const phoneMapped = useMemo(() => Object.values(mapping).includes('phone'), [mapping])
  const rowsWithPhone = useMemo(
    () => mappedRows.filter((r) => String(r.phone || '').trim()).length,
    [mappedRows]
  )
  const tooMany = rows.length > MAX_ROWS

  const handlePush = async () => {
    if (!campaignId || !phoneMapped || tooMany) return
    if (hasLists && !listId) {
      toast.warning(intl.formatMessage(M.toastSelectList))
      return
    }
    setPushing(true)
    setResults(null)

    const total = mappedRows.length
    const allResults: ImportResult[] = []
    let added = 0
    let failed = 0
    let skipped = 0
    let aborted = false

    setProgress({ done: 0, total })

    for (let offset = 0; offset < total; offset += CHUNK_SIZE) {
      const chunk = mappedRows.slice(offset, offset + CHUNK_SIZE)
      try {
        const response = await authPost(
          `${BACKEND_URL}/api/telephony/vicidial/campaigns/push-file`,
          {
            campaign_id: campaignId,
            campaign_name: campaignName,
            list_id: listId,
            rows: chunk,
          }
        )
        const data = response.data
        // Offset row_index so it stays consistent with the full file.
        const chunkResults: ImportResult[] = (data.results || []).map((r: any) => ({
          ...r,
          row_index: (r.row_index ?? 0) + offset,
        }))
        allResults.push(...chunkResults)
        added += data.added || 0
        failed += data.failed || 0
        skipped += data.skipped || 0
      } catch (err: any) {
        toast.error(
          intl.formatMessage(M.toastChunkFailed, {
            from: offset + 1,
            to: offset + chunk.length,
            message: err?.response?.data?.error || err.message,
          })
        )
        // Record the whole chunk as failed and stop.
        chunk.forEach((row, i) => {
          allResults.push({
            row_index: offset + i,
            phone: String(row.phone || ''),
            name: [row.first_name, row.last_name].filter(Boolean).join(' ') || String(row.name || ''),
            status: 'failed',
            reason:
              err?.response?.data?.error || err.message || intl.formatMessage(M.requestFailed),
          })
        })
        failed += chunk.length
        aborted = true
        break
      }
      setProgress({ done: Math.min(offset + CHUNK_SIZE, total), total })
    }

    setResults(allResults)
    setProgress(null)
    setPushing(false)

    if (aborted) return
    if (added > 0)
      toast.success(intl.formatMessage(M.toastLeadsAdded, { count: added, dialer: DIALER_POLICYDIAL }))
    if (failed > 0) toast.warning(intl.formatMessage(M.toastLeadsFailed, { count: failed }))
    if (skipped > 0) toast.info(intl.formatMessage(M.toastLeadsSkipped, { count: skipped }))
  }

  const previewRows = rows.slice(0, 5)
  const addedCount = results?.filter((r) => r.status === 'added').length || 0
  const failedCount = results?.filter((r) => r.status === 'failed').length || 0
  const skippedCount = results?.filter((r) => r.status === 'skipped').length || 0

  if (results) {
    return (
      <div>
        <div className='d-flex gap-3 mb-4'>
          <div className='bg-light rounded p-3 text-center flex-fill'>
            <div className='fs-2 fw-bold text-success'>{addedCount}</div>
            <small className='text-muted'><FormattedMessage {...M.fileStatAdded} /></small>
          </div>
          <div className='bg-light rounded p-3 text-center flex-fill'>
            <div className='fs-2 fw-bold text-danger'>{failedCount}</div>
            <small className='text-muted'><FormattedMessage {...M.statFailed} /></small>
          </div>
          <div className='bg-light rounded p-3 text-center flex-fill'>
            <div className='fs-2 fw-bold text-secondary'>{skippedCount}</div>
            <small className='text-muted'><FormattedMessage {...M.fileStatSkipped} /></small>
          </div>
        </div>
        {(failedCount + skippedCount > 0) && (
          <div className='border rounded mb-3' style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <small className='text-muted fw-bold d-block p-2 bg-light'>
              <FormattedMessage {...M.fileIssues} />
            </small>
            <Table size='sm' className='mb-0'>
              <thead className='table-light'>
                <tr>
                  <th style={{ width: 60 }}><FormattedMessage {...M.fileColRow} /></th>
                  <th><FormattedMessage {...M.phone} /></th>
                  <th><FormattedMessage {...M.name} /></th>
                  <th><FormattedMessage {...M.status} /></th>
                  <th><FormattedMessage {...M.fileColReason} /></th>
                </tr>
              </thead>
              <tbody>
                {results
                  .filter((r) => r.status !== 'added')
                  .map((r) => (
                    <tr key={r.row_index}>
                      <td className='text-muted small'>{r.row_index + 1}</td>
                      <td className='small'>{r.phone || EM_DASH}</td>
                      <td className='small'>{r.name || EM_DASH}</td>
                      <td>
                        <Badge bg={r.status === 'failed' ? 'danger' : 'secondary'}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className='small text-muted'>{r.reason || EM_DASH}</td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        )}
        <Button variant='outline-primary' onClick={reset}>
          <i className='bi bi-arrow-counterclockwise me-1'></i> <FormattedMessage {...M.fileImportAnother} />
        </Button>
      </div>
    )
  }

  return (
    <div>
      {/* Step 1: file picker */}
      <div className='mb-4'>
        <Form.Label className='fw-bold small text-muted mb-2'>
          <FormattedMessage {...M.fileStep1} />
        </Form.Label>
        <div className='d-flex gap-2 align-items-center'>
          <Form.Control
            ref={fileInputRef}
            type='file'
            accept='.csv,.xlsx,.xls'
            onChange={onFileChange}
            disabled={pushing}
          />
          {fileName && (
            <Button variant='outline-secondary' size='sm' onClick={reset} disabled={pushing}>
              <i className='bi bi-x-circle me-1'></i> <FormattedMessage {...M.fileClear} />
            </Button>
          )}
        </div>
        <Form.Text className='text-muted'>
          <FormattedMessage
            {...M.fileTemplateHint}
            values={{
              b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              phoneField: <code>{CSV_PHONE_FIELD}</code>,
            }}
          />
        </Form.Text>
        {parseError && <Alert variant='danger' className='mt-2 mb-0 small'>{parseError}</Alert>}
      </div>

      {/* Step 2: mapping + preview */}
      {headers.length > 0 && (
        <>
          <div className='mb-3'>
            <Form.Label className='fw-bold small text-muted mb-2'>
              <FormattedMessage {...M.fileStep2} />
            </Form.Label>
            <div className='border rounded p-3 bg-light'>
              <div className='row g-2'>
                {headers.map((h) => (
                  <div key={h} className='col-md-6 col-lg-4 d-flex align-items-center gap-2'>
                    <span className='small fw-bold text-truncate flex-fill' title={h}>{h}</span>
                    <i className='bi bi-arrow-right text-muted'></i>
                    <Form.Select
                      size='sm'
                      value={mapping[h] || ''}
                      onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value as FieldKey }))}
                      style={{ maxWidth: 180 }}
                      disabled={pushing}
                    >
                      {FIELD_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{intl.formatMessage(opt.label)}</option>
                      ))}
                    </Form.Select>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className='mb-3'>
            <Form.Label className='fw-bold small text-muted mb-2'>
              <FormattedMessage
                {...M.filePreviewLabel}
                values={{
                  total: rows.length.toLocaleString(),
                  suffix:
                    previewRows.length < rows.length
                      ? intl.formatMessage(M.filePreviewSuffix, { shown: previewRows.length })
                      : '',
                }}
              />
            </Form.Label>
            <div className='border rounded' style={{ maxHeight: '250px', overflow: 'auto' }}>
              <Table size='sm' className='mb-0'>
                <thead className='table-light sticky-top'>
                  <tr>
                    {headers.map((h) => (
                      <th key={h} className='small'>
                        <div>{h}</div>
                        {mapping[h] && (
                          <Badge bg='info' className='small mt-1' style={{ fontSize: '0.65rem' }}>
                            {intl.formatMessage(
                              FIELD_OPTIONS.find((o) => o.value === mapping[h])!.label
                            )}
                          </Badge>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i}>
                      {headers.map((h) => (
                        <td key={h} className='small text-muted'>{String(row[h] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          <div className='d-flex justify-content-between align-items-center gap-3 flex-wrap'>
            <Alert variant={!phoneMapped ? 'danger' : tooMany ? 'danger' : 'info'} className='mb-0 small flex-fill'>
              {!phoneMapped && (
                <>
                  <i className='bi bi-exclamation-triangle me-1'></i>{' '}
                  <FormattedMessage
                    {...M.filePhoneRequired}
                    values={{ b: (chunks: React.ReactNode) => <strong>{chunks}</strong> }}
                  />
                </>
              )}
              {phoneMapped && tooMany && (
                <>
                  <i className='bi bi-exclamation-triangle me-1'></i>{' '}
                  <FormattedMessage
                    {...M.fileTooManyRows}
                    values={{ rows: rows.length.toLocaleString(), max: MAX_ROWS.toLocaleString() }}
                  />
                </>
              )}
              {phoneMapped && !tooMany && (
                <>
                  <i className='bi bi-info-circle me-1'></i>{' '}
                  <FormattedMessage
                    {...M.fileReadyToPush}
                    values={{
                      ready: rowsWithPhone.toLocaleString(),
                      total: rows.length.toLocaleString(),
                      campaign: campaignName,
                      chunk: CHUNK_SIZE,
                      b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                    }}
                  />
                </>
              )}
            </Alert>
            <Button
              variant='primary'
              disabled={
                !phoneMapped ||
                tooMany ||
                pushing ||
                rowsWithPhone === 0 ||
                (hasLists && !listId)
              }
              onClick={handlePush}
            >
              {pushing ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />{' '}
                  <FormattedMessage
                    {...M.filePushing}
                    values={{
                      progress: progress
                        ? `${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}`
                        : ELLIPSIS,
                    }}
                  />
                </>
              ) : (
                <>
                  <i className='bi bi-send me-1'></i>{' '}
                  <FormattedMessage
                    {...M.filePush}
                    values={{ count: rowsWithPhone.toLocaleString(), dialer: DIALER_VICIDIAL }}
                  />
                </>
              )}
            </Button>
          </div>

          {progress && (
            <div
              className='mt-3 border border-warning rounded p-3 bg-warning-subtle'
              style={{ position: 'sticky', bottom: 0, zIndex: 5 }}
            >
              <div className='d-flex align-items-center gap-2 mb-2'>
                <Spinner animation='border' size='sm' variant='warning' />
                <strong className='text-warning-emphasis'>
                  <FormattedMessage
                    {...M.fileUploadingWarning}
                    values={{ count: progress.total.toLocaleString() }}
                  />
                </strong>
              </div>
              <ProgressBar
                now={progress.total > 0 ? (progress.done / progress.total) * 100 : 0}
                label={`${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}`}
                animated
                striped
                variant='warning'
              />
              <small className='text-muted d-block mt-1'>
                <FormattedMessage
                  {...M.filePushingChunk}
                  values={{
                    current: Math.min(
                      Math.floor(progress.done / CHUNK_SIZE) + 1,
                      Math.ceil(progress.total / CHUNK_SIZE)
                    ),
                    total: Math.ceil(progress.total / CHUNK_SIZE),
                  }}
                />
              </small>
            </div>
          )}
        </>
      )}
    </div>
  )
}
